import { capitalize } from '@/lib/collections';
import { ChevronRight, AlertTriangle } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

import { PictoVoirDetail } from '@/assets/icons-april-fr/pictos';
import SmallSpinner from '@/components/shared/spinners/small-spinner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableRow } from '@/components/ui/table';
import { useTiersAccidents } from '@/hooks/api/use-accidents';
import { useSelectedContract } from '@/hooks/api/use-contracts';
import { isACLContract } from '@/lib/contracts';

/** Bloc « Mes sinistres » de l'accueil : sinistres du tiers, relus à chaque affichage. */
const Accidents = () => {
  const navigate = useNavigate();
  const accidentsQuery = useTiersAccidents({ refetchOnMount: 'always' });
  const isACL = isACLContract(useSelectedContract());

  // Ancien loader : message d'erreur tant que la dernière lecture a échoué, spinner pendant la lecture.
  if (accidentsQuery.isError) return <p>Erreur dans le chargement</p>;
  if (accidentsQuery.isPending || accidentsQuery.isFetching) return <SmallSpinner />;

  const accidents = accidentsQuery.data ?? [];

  const getStatusVariant = (situationCode?: string) => {
    switch (situationCode) {
      case '1': return 'warning';
      case '2':
      case '8': return 'destructive';
      case 'A': return 'warning';
      case '3': return 'secondary';
      default: return 'secondary';
    }
  };

  return (
    <Card data-tutorial="home-accidents" className="relative">
      <Link
        to="/mes-sinistres"
        className="absolute top-6 right-6 text-sm text-muted-foreground transition-colors hover:text-foreground no-underline inline-flex items-center gap-1"
      >
        Tous les sinistres
        <ChevronRight className="w-4 h-4" aria-hidden="true" />
      </Link>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg">Mes sinistres</CardTitle>
      </CardHeader>
      <CardContent className="px-0">
        {accidents.length >= 1 ? (
          <>
          {/* Mobile: liste empilée */}
          <div className="sm:hidden max-h-[220px] overflow-y-auto divide-y">
            {accidents.map((a, i) => (
              <div
                key={i}
                className="flex items-center justify-between gap-3 px-6 py-4 cursor-pointer hover:bg-muted/50 transition-colors"
                onClick={() => navigate(`/mes-sinistres/contrat/${a.id_contrat}/sinistre/${a.id}`)}
              >
                <p className="font-medium text-sm">Sinistre n°{a.numCbt}</p>
                <div className="flex items-center gap-2 shrink-0">
                  <Badge variant={getStatusVariant(a.situationCode)} className="text-xs">
                    {capitalize(a.situation)}
                  </Badge>
                  <ChevronRight className="w-4 h-4 text-muted-foreground" aria-hidden="true" />
                </div>
              </div>
            ))}
          </div>

          {/* Desktop: tableau de mise en forme (pas de structure tabulaire signifiante) */}
          <div className="hidden sm:block max-h-[180px] overflow-y-auto">
          <Table role="presentation">
            <TableBody>
              {accidents.map((a, i) => (
                <TableRow
                  key={i}
                  className="[&_td]:align-middle cursor-pointer"
                  onClick={() => navigate(`/mes-sinistres/contrat/${a.id_contrat}/sinistre/${a.id}`)}
                >
                  <TableCell className="font-medium !pl-6">
                    Sinistre n°{a.numCbt}
                  </TableCell>
                  <TableCell>
                    <Badge variant={getStatusVariant(a.situationCode)} className="text-sm">
                      {capitalize(a.situation)}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right !pr-6">
                    <Link
                      to={`/mes-sinistres/contrat/${a.id_contrat}/sinistre/${a.id}`}
                      className="inline-flex items-center justify-center w-9 h-9 rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                      onClick={(e) => e.stopPropagation()}
                      aria-label={`Voir le sinistre n°${a.numCbt}`}
                    >
                      <PictoVoirDetail className="w-4 h-4" aria-hidden="true" />
                    </Link>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
          </>
        ) : (
          <p className="text-sm px-6 py-6 text-muted-foreground">Vous n'avez actuellement aucun sinistre déclaré.</p>
        )}

      </CardContent>

      {/* Déclarer un sinistre button */}
      {!isACL && (
        <div className="px-6 py-6 border-t mt-auto">
          <Button asChild className="w-full cursor-pointer !text-white py-4">
            <Link to="/mes-sinistres/je-declare-un-sinistre">
              <AlertTriangle className="w-4 h-4" aria-hidden="true" />
              Déclarer un sinistre
            </Link>
          </Button>
        </div>
      )}
    </Card>
  );
};

export default Accidents;
